import { MessageCircle, Phone, MapPin, Factory, ArrowUp, Instagram } from "lucide-react";
import { Reveal, Chapter } from "./Reveal";
import { scrollToSection } from "@/lib/scroll";
import { whatsappUrl, CALL_URL, PHONE_DISPLAY, INSTAGRAM_URL } from "@/data/company";
import { UnitMaps } from "./UnitMaps";

const UNITS = [
  { name: "Garg Stone Company", addr: "G-329, Road No. 6, RIICO IPIA, Kota 324005 · H1-226(K), RIICO IPIA, Kota" },
  { name: "Garg Minerals · Ramganj Mandi", addr: "E-45, Industrial Area, Gundi Fatehpur, Ramganj Mandi 326529" },
  { name: "Garg Minerals · Ranpur", addr: "E-249, Kuber Extn., RIICO, Ranpur, Kota" },
];

const Contact = () => (
  <footer id="contact" className="relative bg-coal border-t border-bone/10" data-testid="contact-section">
    <div className="px-6 md:px-12 lg:px-20 pt-28 md:pt-36 pb-16">
      <Reveal>
        <Chapter index="08" title="Contact" testid="contact-chapter" />
      </Reveal>

      <Reveal delay={0.1}>
        <h2
          className="contact-title mt-10 font-heading font-black uppercase leading-[1]"
          data-testid="contact-heading"
        >
          Let's <span className="text-stroke">talk</span>
          <span className="font-editorial italic font-medium normal-case text-copper text-[0.5em] align-middle"> stone.</span>
        </h2>
      </Reveal>

      <Reveal delay={0.2} className="mt-6 max-w-xl">
        <p className="text-base md:text-lg font-light text-bone/60 leading-relaxed">
          Tell us the stone, the finish and the quantity. We'll come back with
          rates, freight and an honest timeline — from the yard, not a call centre.
        </p>
      </Reveal>

      <Reveal delay={0.28} className="mt-12 flex flex-wrap items-center gap-5">
        <a
          href={whatsappUrl()}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Get a quote on WhatsApp"
          data-testid="whatsapp-cta"
          className="group relative overflow-hidden inline-flex items-center gap-3 bg-copper px-6 sm:px-10 py-5 text-xs uppercase font-heading font-bold text-ink"
        >
          <span className="relative z-10 flex items-center gap-3 transition-colors duration-300 group-hover:text-bone">
            <MessageCircle size={17} /> WhatsApp a Quote
          </span>
          <span className="absolute inset-0 bg-ink translate-y-full transition-transform duration-300 ease-out group-hover:translate-y-0" />
        </a>
        <a
          href={CALL_URL}
          aria-label={`Call Garg Stone Company on ${PHONE_DISPLAY}`}
          data-testid="call-cta"
          className="inline-flex items-center gap-3 border border-bone/30 px-6 sm:px-10 py-5 text-xs uppercase font-heading font-bold transition-colors duration-300 hover:border-copper hover:text-copper"
        >
          <Phone size={17} className="shrink-0" /> {PHONE_DISPLAY}
        </a>
      </Reveal>

      <Reveal delay={0.34}>
        <div className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-10 border-t border-bone/10 pt-12" data-testid="contact-info-grid">
          {UNITS.map((u, i) => (
            <div key={u.name} className="flex gap-4" data-testid={`contact-unit-${i}`}>
              <Factory size={18} className="mt-1 shrink-0 text-copper" />
              <div>
                <p className="text-xs tracking-[0.25em] uppercase font-heading text-bone/50 mb-2">{u.name}</p>
                <p className="text-sm font-light text-bone/75 leading-relaxed">{u.addr}</p>
              </div>
            </div>
          ))}
        </div>
      </Reveal>

      <Reveal delay={0.4}>
        <p className="mt-10 flex items-center gap-3 text-xs font-light text-bone/40" data-testid="contact-production-note">
          <MapPin size={14} className="text-copper" />
          Over 1,00,000 sq ft of production area across Kota — visits welcome by appointment.
        </p>
      </Reveal>
      <UnitMaps />
    </div>

    <div className="border-t border-bone/10 px-6 md:px-12 lg:px-20 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
      <p className="text-xs font-light text-bone/40" data-testid="footer-copyright">
        © 2026 Garg Stone Company — Kota, Rajasthan · Since 1988
      </p>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Garg Stone Company on Instagram"
          data-testid="footer-instagram-link"
          className="inline-flex items-center gap-2 whitespace-nowrap text-xs tracking-[0.25em] uppercase font-heading text-bone/50 transition-colors duration-300 hover:text-copper"
        >
          <Instagram size={15} /> @gargstonecompany
        </a>
        <button
          onClick={() => scrollToSection("#top")}
          className="inline-flex items-center gap-2 whitespace-nowrap text-xs tracking-[0.25em] uppercase font-heading text-bone/50 transition-colors duration-300 hover:text-copper"
          aria-label="Back to top"
          data-testid="back-to-top-button"
        >
          Back to top <ArrowUp size={14} />
        </button>
      </div>
    </div>
  </footer>
);

export default Contact;
